import { GridSortModel } from '@mui/x-data-grid/models/gridSortModel';
import { format } from 'date-fns';
import {
  BehaviorSubject,
  filter,
  map,
  merge,
  Observable,
  of,
  share,
  switchMap,
  take,
  tap,
} from 'rxjs';

import {
  C_Incoming_Orders_Source,
  C_Report_Group,
  PrintProductionReportParamsInput,
  ListWa_ProductionPrintJobsQueryVariables as ProductionPrintJobsFilter,
} from '../../../../graphql/generatedModel.ts';
import { dataHelper, TypeData } from '../../../../shared/helpers/data/data.helper.ts';
import { storageHelper } from '../../../../shared/helpers/storage/index.ts';
import { companyConfigService } from '../../../../shared/services/companyConfig/companyConfig.service.ts';
import { Pub, State, Sub } from '../../../../shared/state/state.abstract.ts';
import {
  getDataStubInCaseOfError,
  storageFilter,
  storagePrintDeliveryNotesSortModel,
  storageProductionSortModel,
} from '../../loaders/printJobs/printJobs.loader.ts';
import {
  ChangeIncomingOrdersStatusRes,
  CountOrderPreProductionRes,
  CountOrdersRes,
  FullPrintJobsDataRes,
  PrintDeliveryNotesTabRes,
  PrintJobsProductionTabRes,
  printJobsService,
  PrintProductionReportsRes,
  ProductionListRes,
  ProductionPrintDeliveryNotesRes,
  StatusBakery2bCAdirectRes,
} from '../../services/printJobs.service.ts';
import { StateHelpers } from './printJobsDetails.helpers.ts';
import { responseHandler } from '../../../../shared/responseHandler/responseHandler.ts';
import { localeFormatterHelper } from '../../../../shared/helpers/formatter/localeFormatter.helper.ts';
import i18n from '../../../../i18n/init.ts';
import { snackbarService } from '../../../../shared/components/snackbar/service/snackbar.service.ts';
import {
  editOrderService,
  PrintOrderReportsRes,
} from '../../../order/services/editOrder.service.ts';
import {
  responseErrorHandlerForReportsPrinting,
  successHandlerForReportsPrinting,
} from '../../../../shared/services/reports/reports.helper';

export enum PrintJobsTabs {
  production = 'production',
  printDeliveryNotes = 'print-delivery-notes',
}
export enum ProductionFilterListType {
  fullList = 'fullList',
  activeLists = 'activeLists',
  inactiveLists = 'inactiveLists',
}
export const initProductionFilter = {
  printListsActiveState: true,
  reportGroupIds: [
    C_Report_Group.RG1_PRODUCTION_PRODUCTION,
    C_Report_Group.RG8_PRODUCTION_FORWARDING_LISTS,
    C_Report_Group.RG9_PRODUCTION_CROSSTABLE,
    C_Report_Group.RG3_ARTICLE,
    C_Report_Group.RG11_PRODUCTION_SMARTSCALE,
  ],
  groupId: null,
};
export const tabs: IDataTabs = {
  [PrintJobsTabs.production]: undefined,
  [PrintJobsTabs.printDeliveryNotes]: undefined,
};
export type IDataTabs = {
  [tabKey in PrintJobsTabs]: IProductionTabDataRes | IDeliveryNotesTabDataRes | undefined;
};

const defaultDate = format(localeFormatterHelper.localizedDate(), 'yyyy-MM-dd');
const date = companyConfigService.getCachedConfigs()?.nextDeliveryDate || defaultDate;
const fromDate = date;
const toDate = date;

export const defaultProductionFilter = {
  fromDate,
  toDate,
  ...(storageFilter || initProductionFilter),
};
export const initPrintJobsDetailsState: IPrintJobsDetailsState = {
  action: undefined,
  dataTabs: { ...tabs },
  loading: false,
  selectedTab: PrintJobsTabs.production,
  statusList: {
    bakery2b: false,
    CAdirect: false,
  },
  datePeriod: {
    fromDate,
    toDate,
  },
};
export const initPrintJobsDetailsSortModel = (field: string): GridSortModel => [
  { field, sort: 'asc' },
];

class PubImpl extends Pub<IPrintJobsDetailsState> {
  init(params: Partial<IPrintJobsDetailsState>) {
    this.emit('init', params);
  }
  search(search: string) {
    const { dataTabs, selectedTab } = this.stream$.getValue();
    dataTabs[selectedTab]!.params.search = search;
    this.emit('search', { dataTabs });
  }
  printProductionReports(reports: PrintProductionReportParamsInput[]) {
    printJobsService.pub.printProductionReports({ reports });
    this.emit('printProductionReports', {});
  }
  printDeliveryNotes(ordersIds: string[]) {
    this.emit('printDeliveryNotes', { params: ordersIds });
  }
  sort(sortModel: GridSortModel) {
    const { dataTabs, selectedTab } = this.stream$.getValue();
    dataTabs[selectedTab]!.params.sortModel = sortModel;
    this.emit('sort', { dataTabs });
    storageHelper.local.setItem(
      selectedTab === PrintJobsTabs.production
        ? 'printJobs.productionSortModel'
        : 'printJobs.printDeliveryNotesSortModel',
      sortModel,
    );
  }
  filter(filter: IProductionTabDataRes['filter']) {
    const { dataTabs, selectedTab } = this.stream$.getValue();
    this.emit('filter', {
      dataTabs: {
        ...dataTabs,
        production: {
          ...dataTabs[selectedTab],
          filter,
        },
      },
    });
    storageHelper.session.setItem('printJobs.productionFilterValue', filter);
  }
  selectDatePeriod(date: string) {
    const filter =
      storageHelper.session.getItem('printJobs.productionFilterValue') || initProductionFilter;
    this.emit('selectDatePeriod', { datePeriod: { fromDate: date, toDate: date } });
    storageHelper.session.setItem('printJobs.productionFilterValue', {
      ...filter,
      fromDate: date,
      toDate: date,
    });
  }
  changeStatusBakery2B(checked: boolean) {
    const { statusList } = this.stream$.getValue();
    this.emit('changeStatusBakery2B', {
      statusList: {
        ...statusList,
        bakery2b: checked,
      },
    });
  }
  changeStatusCAdirect(checked: boolean) {
    const { statusList } = this.stream$.getValue();
    this.emit('changeStatusCAdirect', {
      statusList: {
        ...statusList,
        CAdirect: checked,
      },
    });
  }
  dataForSelectedTab(tab: PrintJobsTabs) {
    this.emit('dataForSelectedTab', { selectedTab: tab });
  }
  productionList() {
    this.emit('productionList', {});
  }
  selectProductionRecord(selectedRecord: IProductionTabDataRes['selectedRecord']) {
    const { dataTabs } = this.stream$.getValue();
    if (dataTabs.production) {
      const production = dataTabs.production as IProductionTabDataRes;
      production.selectedRecord = selectedRecord;
      dataTabs.production = production;
      this.emit('selectProductionRecord', { dataTabs });
    }
  }
  navigateInProductionList(navTo: 'next' | 'previous') {
    const { dataTabs } = this.stream$.getValue();
    const production = dataTabs.production as IProductionTabDataRes;
    production.navTo = navTo;
    dataTabs.production = production;
    this.emit('productionListNav', { dataTabs });
  }
}

class SubImpl extends Sub<IPrintJobsDetailsState> {
  private listLoading$ = new BehaviorSubject<boolean>(false);
  private shareListLoading$: Observable<boolean> = this.listLoading$.pipe(share());

  private tabLoading$ = new BehaviorSubject<boolean>(false);
  private shareTabLoading$: Observable<boolean> = this.tabLoading$.pipe(share());

  protected actionHandlers(): Observable<IPrintJobsDetailsState> {
    return merge(
      this.updateState(),
      this.dataForSelectedTab(),
      this.listenForStateUpdates(),
      this.printProductionReports(),
      this.printDeliveryNotes(),
      this.changeStatusListValue(),
    ).pipe(
      map(({ dataTabs, selectedTab, ...rest }) => {
        const deepCopyOfDataTabs = structuredClone(dataTabs);
        const selectedTabData = deepCopyOfDataTabs[selectedTab] as TypeData[number];
        // The selected tab's data may not be loaded yet — e.g. `init` fires on a
        // deep-linked / refreshed route (`/production/print-jobs/print-delivery-notes`)
        // before the tab payload lands in the stream. Skip the sort/search transform
        // in that case instead of dereferencing `undefined` (`...params.sortModel`)
        // and crashing (WA-3652).
        if (!selectedTabData?.params) {
          return { ...rest, dataTabs: deepCopyOfDataTabs, selectedTab };
        }
        switch (selectedTab) {
          case PrintJobsTabs.production:
            selectedTabData.allProductionListLength = selectedTabData!.productionList.length;
            selectedTabData!.productionList = this.updateDataTabByParams(
              deepCopyOfDataTabs,
              selectedTab,
              'productionList',
              ['name'],
            );
            break;
          case PrintJobsTabs.printDeliveryNotes:
            selectedTabData!.printDeliveryNotesList = this.updateDataTabByParams(
              deepCopyOfDataTabs,
              selectedTab,
              'printDeliveryNotesList',
            );
            break;
          default:
            break;
        }
        return { ...rest, dataTabs: deepCopyOfDataTabs, selectedTab };
      }),
      map((streamV) => {
        const { action, dataTabs } = streamV;
        if (
          action === 'dataForSelectedTab' ||
          action === 'productionList' ||
          action === 'filter' ||
          action === 'selectDatePeriod' ||
          action === 'search'
        ) {
          const productionData = dataTabs.production as IProductionTabDataRes | undefined;
          if (productionData !== undefined) {
            streamV.dataTabs.production = { ...productionData, selectedRecord: undefined };
          }
        }
        return streamV;
      }),
      filter((v) => {
        if (v.action === 'productionListNav') {
          // List navigation handler should go after list mutation
          // such as search, filter implemented in this.updateDataTabByParams
          this.handleNavInProductionList(v);
          return false;
        }
        return true;
      }),
      tap(() => {
        this.tabLoading$.next(false);
        this.listLoading$.next(false);
      }),
    );
  }

  listLoading(): Observable<boolean> {
    return this.shareListLoading$;
  }

  tabLoading(): Observable<boolean> {
    return this.shareTabLoading$;
  }

  sharedValues(): Observable<IPrintJobsDetailsState> {
    return of(this.stream$.getValue());
  }

  /* PRIVATE METHODS */
  private updateState(): Observable<IPrintJobsDetailsState> {
    return this.actionListener([
      'init',
      'search',
      'sort',
      'selectProductionRecord',
      'productionListNav',
    ]);
  }
  private printProductionReports(): Observable<IPrintJobsDetailsState> {
    return this.actionListener('printProductionReports').pipe(
      tap(() => {
        snackbarService.pub.show({
          type: 'loading',
          content: i18n.t('common.sending_to_printer'),
          noAutoHide: true,
        });
      }),
      switchMap((stateV) => {
        return printJobsService.sub.printProductionReports().pipe(
          responseHandler<PrintProductionReportsRes>({
            errorReturnType: {
              countCreated: 0,
              countErrors: 0,
            },
            quite: true,
            customErrorHandler: responseErrorHandlerForReportsPrinting,
            success: successHandlerForReportsPrinting,
          }),
          map(() => {
            return stateV;
          }),
        );
      }),
    );
  }
  private printDeliveryNotes(): Observable<IPrintJobsDetailsState> {
    return this.actionListener('printDeliveryNotes').pipe(
      tap(() => {
        snackbarService.pub.show({
          type: 'loading',
          content: i18n.t('common.sending_to_printer'),
          noAutoHide: true,
        });
      }),
      switchMap((stateV) => {
        const ordersIds = stateV.params || [];

        return editOrderService.globHelpers.streamToPromise(
          editOrderService.sub.printOrderReports().pipe(
            responseHandler<PrintOrderReportsRes>({
              errorReturnType: {
                countCreated: 0,
                countErrors: 0,
                successfullyPrintedOrderIds: [],
              },
              quite: true,
              customErrorHandler: responseErrorHandlerForReportsPrinting,
              success: successHandlerForReportsPrinting,
            }),
            take(1),
            map((response) => {
              const successfullyPrintedOrderIds = response.successfullyPrintedOrderIds ?? [];
              const printedDateTime = localeFormatterHelper.localizedDate();
              const needChangePrintedStatus =
                successfullyPrintedOrderIds.length > 0 &&
                stateV?.dataTabs?.['print-delivery-notes'];

              if (needChangePrintedStatus) {
                const updatedState = structuredClone(stateV);
                const deliveryNotesTab = updatedState.dataTabs[
                  'print-delivery-notes'
                ] as IDeliveryNotesTabDataRes;

                deliveryNotesTab.printDeliveryNotesList =
                  deliveryNotesTab.printDeliveryNotesList.map((order) => {
                    if (successfullyPrintedOrderIds.includes(order.id)) {
                      return {
                        ...order,
                        isPrinted: true,
                        printedDateTime,
                      };
                    }
                    return order;
                  });

                updatedState.dataTabs['print-delivery-notes'] = deliveryNotesTab;
                return updatedState;
              }
              return stateV;
            }),
          ),
          () => editOrderService.pub.printOrderReports({ ordersIds }),
        );
      }),
    );
  }

  private changeStatusListValue(): Observable<IPrintJobsDetailsState> {
    return this.actionListener(['changeStatusBakery2B', 'changeStatusCAdirect']).pipe(
      tap((state) => {
        const {
          statusList,
          datePeriod: { fromDate },
          action,
        } = state;
        this.selectMutationForStatusListValues(statusList, fromDate, action)
          .pipe(take(1))
          .subscribe();
      }),
    );
  }
  private dataForSelectedTab(): Observable<IPrintJobsDetailsState> {
    return this.actionListener('dataForSelectedTab').pipe(
      switchMap((state) => {
        const { dataTabs, selectedTab, datePeriod } = state;
        if (dataTabs[selectedTab]?.params) {
          dataTabs[selectedTab]!.params.search = '';
        }
        if (!dataTabs[selectedTab]) {
          this.tabLoading$.next(true);
          const sortModel =
            (selectedTab === PrintJobsTabs.production
              ? storageProductionSortModel
              : storagePrintDeliveryNotesSortModel) ||
            initPrintJobsDetailsSortModel(
              selectedTab === PrintJobsTabs.production ? 'name' : 'orderNo',
            );
          return this.queryDataForTab(selectedTab, datePeriod).pipe(
            map((dataTab) => {
              state.dataTabs[selectedTab] = {
                ...dataTab,
                params: { sortModel, search: '' },
                ...(selectedTab === PrintJobsTabs.production && {
                  filter: defaultProductionFilter,
                }),
              } as IProductionTabDataRes | IDeliveryNotesTabDataRes;
              return state;
            }),
          );
        }
        return of(state);
      }),
    );
  }
  private listenForStateUpdates(): Observable<IPrintJobsDetailsState> {
    return this.actionListener(['productionList', 'filter', 'selectDatePeriod']).pipe(
      tap(({ dataTabs, selectedTab, datePeriod: { fromDate, toDate }, action }) => {
        this.listLoading$.next(true);
        const filterValue = (dataTabs[selectedTab] as IProductionTabDataRes)?.filter;
        let filter: ProductionPrintJobsFilter['filter'] | undefined;
        if (filterValue) {
          filter = {
            ...filterValue,
            fromDate,
            toDate,
          };
        }
        this.publishDataBasedOnAction(action, selectedTab, { fromDate, toDate }, filter);
      }),
      switchMap((state) => this.fetchAndUpdateStateBasedOnAction(state)),
      map((state) => {
        const productionTabData = state.dataTabs.production as IProductionTabDataRes | undefined;
        if (productionTabData) {
          productionTabData.selectedRecord = undefined;
          state.dataTabs.production = productionTabData;
        }
        return state;
      }),
    );
  }

  /* Private non merged methods */
  private updateDataTabByParams(
    dataTabs: IDataTabs,
    selectedTab: PrintJobsTabs,
    tabDataKey: 'productionList' | 'printDeliveryNotesList',
    fieldToSearch?: string[],
  ) {
    const currentData = (dataTabs[selectedTab] as TypeData[number])?.[tabDataKey];
    const updatedData = dataHelper
      .data(currentData)
      .sort({
        sortModel: dataTabs[selectedTab]!.params.sortModel,
        callback: (data) => {
          (this.stream$.value.dataTabs[selectedTab] as TypeData[number])![tabDataKey] = data || [];
        },
      })
      .search({
        search: dataTabs[selectedTab]!.params.search,
        fields: fieldToSearch as string[],
      })
      .result();
    return updatedData;
  }
  private queryDataForTab(
    selectedTab: PrintJobsTabs,
    datePeriod: IPrintJobsDetailsState['datePeriod'],
  ): Observable<
    | Pick<IProductionTabDataRes, 'productionList' | 'countOrders' | 'countOrderPreProduction'>
    | Pick<IDeliveryNotesTabDataRes, 'printDeliveryNotesList'>
    | undefined
  > {
    const { fromDate, toDate } = datePeriod || {};
    switch (selectedTab) {
      case PrintJobsTabs.production:
        printJobsService.pub.printJobsProductionTabData({
          filter: { ...defaultProductionFilter, fromDate, toDate },
          fromDate,
          toDate,
        });
        break;
      case PrintJobsTabs.printDeliveryNotes:
        printJobsService.pub.printJobsDeliveryNotesTabData({ fromDate, toDate });
        break;
      default:
        return of(undefined);
    }
    return merge(
      printJobsService.sub.printJobsProductionTabData().pipe(
        responseHandler<PrintJobsProductionTabRes>({
          errorReturnType: {
            productionList: [],
            countOrders: 0,
            countOrderPreProduction: 0,
          },
        }),
      ),
      printJobsService.sub.printJobsDeliveryNotesTabData().pipe(
        responseHandler<PrintDeliveryNotesTabRes>({
          errorReturnType: {
            printDeliveryNotesList: [],
          },
        }),
      ),
    );
  }
  private selectMutationForStatusListValues(
    statusList: IPrintJobsDetailsState['statusList'],
    onDate: IPrintJobsDetailsState['datePeriod']['fromDate'],
    action: IPrintJobsDetailsState['action'],
  ): Observable<ChangeIncomingOrdersStatusRes | undefined> {
    switch (action) {
      case 'changeStatusBakery2B':
        printJobsService.pub.changeIncomingOrdersStatus({
          externalSourceKind: C_Incoming_Orders_Source.IOS0_bakery2b,
          onDate,
          isAccepted: statusList.bakery2b,
        });
        break;
      case 'changeStatusCAdirect':
        printJobsService.pub.changeIncomingOrdersStatus({
          externalSourceKind: C_Incoming_Orders_Source.IOS1_CashAssistDirect,
          onDate,
          isAccepted: statusList.CAdirect,
        });
        break;
      default:
        return of(undefined);
    }
    return merge(
      printJobsService.sub
        .changeIncomingOrdersStatus()
        .pipe(responseHandler<ChangeIncomingOrdersStatusRes>({ errorReturnType: false })),
    );
  }
  private publishDataBasedOnAction(
    action: IPrintJobsDetailsState['action'],
    selectedTab: PrintJobsTabs,
    datePeriod: IPrintJobsDetailsState['datePeriod'],
    productionFilter?: ProductionPrintJobsFilter['filter'],
  ): void {
    switch (action) {
      case 'selectDatePeriod':
        printJobsService.pub.fullPrintJobsDataDependedOnSelectedTab({
          selectedTab,
          datePeriod,
          filter: productionFilter,
        });
        break;

      default:
        // All other actions are responsible only for production list
        printJobsService.pub.printJobsProductionTabData({
          filter: productionFilter as ProductionPrintJobsFilter['filter'],
          fromDate: datePeriod.fromDate,
          toDate: datePeriod.toDate,
        });
    }
  }
  private fetchAndUpdateStateBasedOnAction(
    state: IPrintJobsDetailsState,
  ): Observable<IPrintJobsDetailsState> {
    switch (state.action) {
      case 'selectDatePeriod':
        state.dataTabs[
          state.selectedTab === PrintJobsTabs.production ? 'print-delivery-notes' : 'production'
        ] = undefined;
        return printJobsService.sub.fullPrintJobsDataDependedOnSelectedTab().pipe(
          responseHandler<FullPrintJobsDataRes>({
            errorReturnType: getDataStubInCaseOfError(state.selectedTab),
          }),
          map(({ dataTab, statusList }) => {
            state.statusList = statusList;
            return this.mapDataTabs(dataTab, state);
          }),
        );
      default:
        return printJobsService.sub.printJobsProductionTabData().pipe(
          responseHandler<PrintJobsProductionTabRes>({
            errorReturnType: {
              productionList: [],
              countOrders: 0,
              countOrderPreProduction: 0,
            },
          }),
          map((dataTab) => this.mapDataTabs(dataTab, state)),
        );
    }
  }
  private mapDataTabs(
    data: PrintJobsProductionTabRes | PrintDeliveryNotesTabRes,
    state: IPrintJobsDetailsState,
  ) {
    const { dataTabs, selectedTab, datePeriod } = state || {};
    dataTabs[selectedTab] = {
      ...dataTabs[selectedTab],
      ...data,
      ...(selectedTab === PrintJobsTabs.production && {
        filter: {
          // `dataTabs[selectedTab]` can still be undefined here when the
          // production tab's data has not been loaded yet — e.g. a date-period
          // change fires on a deep-linked/refreshed
          // `/production/print-jobs/production` route before the initial tab
          // payload lands (WA-3700, same "tab not loaded yet" race as WA-3652).
          // Fall back to the default filter instead of dereferencing `.filter`
          // on undefined and crashing.
          ...((dataTabs[selectedTab] as IProductionTabDataRes | undefined)?.filter ??
            defaultProductionFilter),
          fromDate: datePeriod.fromDate,
          toDate: datePeriod.toDate,
        },
      }),
    } as IProductionTabDataRes | IDeliveryNotesTabDataRes;
    return { ...state, dataTabs, selectedTab, datePeriod };
  }
  private handleNavInProductionList(streamV: IPrintJobsDetailsState): void {
    const { dataTabs } = streamV;
    if (dataTabs.production) {
      const { productionList, selectedRecord, navTo } =
        dataTabs.production as IProductionTabDataRes;
      // Disable navigation without currently selected record
      if (!selectedRecord || !navTo) return;

      const selectableRecords: ProductionListRes = productionList.filter(
        (item) =>
          printJobsDetailsState.helpers.isReportForPreview(item.reportId) &&
          printJobsDetailsState.helpers.isReportPreviewEnabled(item),
      );
      const currentInd = selectableRecords.findIndex((item) => item.id === selectedRecord.id);
      if (currentInd === -1) return;
      const navToRecord: ProductionListRes[number] | undefined =
        selectableRecords[navTo === 'next' ? currentInd + 1 : currentInd - 1];
      if (navToRecord) {
        printJobsDetailsState.pub.selectProductionRecord(navToRecord);
      }
    }
  }
}
class PrintJobsState extends State<IPrintJobsDetailsState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
  helpers = new StateHelpers(this.stream$);
}

export const printJobsDetailsState = new PrintJobsState(initPrintJobsDetailsState);

export interface IProductionTabDataRes extends TCommonTableParams {
  productionList: ProductionListRes;
  selectedRecord: ProductionListRes[number] | undefined;
  navTo: 'next' | 'previous' | undefined;
  countOrders: CountOrdersRes;
  countOrderPreProduction: CountOrderPreProductionRes;
  filter: ProductionPrintJobsFilter['filter'];
  allProductionListLength: number;
}

export interface IDeliveryNotesTabDataRes extends TCommonTableParams {
  printDeliveryNotesList: ProductionPrintDeliveryNotesRes;
}

export type TabDataRes = IDataTabs[keyof IDataTabs];

type TCommonTableParams = {
  params: {
    sortModel: GridSortModel;
    search: string;
  };
};

export interface IPrintJobsDetailsState {
  action:
    | 'init'
    | 'sort'
    | 'search'
    | 'filter'
    | 'selectDatePeriod'
    | 'dataForSelectedTab'
    | 'selectProductionRecord'
    | 'productionListNav'
    | 'printProductionReports'
    | 'printDeliveryNotes'
    | 'changeStatusBakery2B'
    | 'changeStatusCAdirect'
    | 'productionList'
    | 'loader'
    | undefined;
  params?: any;
  dataTabs: IDataTabs;
  loading: boolean;
  statusList: StatusBakery2bCAdirectRes;
  selectedTab: PrintJobsTabs;
  datePeriod: {
    fromDate: string;
    toDate: string;
  };
}
